// StageLinkManagerLayout.tsx
import React, { useCallback, useMemo, useState } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  IconButton,
  AppBar,
  Toolbar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Drawer,
  Button,
  useMediaQuery,
  Fab,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import BuildRoundedIcon from "@mui/icons-material/BuildRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ForumIcon from "@mui/icons-material/Forum";
import PollIcon from "@mui/icons-material/Poll";
import CasinoIcon from "@mui/icons-material/Casino";
import PeopleIcon from "@mui/icons-material/People";
import YouTubeIcon from "@mui/icons-material/YouTube";
import MusicVideoIcon from "@mui/icons-material/MusicVideo";
import QueueMusicIcon from "@mui/icons-material/QueueMusic";
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import RemoveCircleOutlineRoundedIcon from "@mui/icons-material/RemoveCircleOutlineRounded";

import LiveChat from "./LiveChat";
import LivePolls from "./LivePolls";
import BingoManagement from "./Talent/BingoManagement";
import GuestManagement from "./Talent/GuestManagement_updated";
import YoutubeRequest from "./Talent/YoutubeRequest";
import Sing4YouManagementUpdated from "./Talent/Sing4YouManagement_updated";
import ListofSongsManagement, { SongItem } from "./Talent/ListofSongsManagement";
import VideoController from "./VideoController";
import LiveStreamsViewer from "./LiveStreamsViewer";
import { ZegoExpressEngine } from "zego-express-engine-webrtc";
import { useSearchParams } from "react-router-dom";

/** ---------------- Netflix-like tokens ---------------- */
const COLORS = {
  bg: "#0b0b0b",
  panel: "#141414",
  panelAlt: "#1c1c1c",
  border: "rgba(255,255,255,0.12)",
  text: "#ffffff",
  textMuted: "rgba(255,255,255,0.74)",
  red: "#E50914",
  redHover: "#b20710",
};
const RADIUS = 2; // 16px

/** ---------------- Shared surface wrapper ---------------- */
function Surface({
  children,
  sx,
  as = Paper,
}: {
  children: React.ReactNode;
  sx?: any;
  as?: any;
}) {
  const Comp = as;
  return (
    <Comp
      elevation={0}
      sx={{
        p: 2,
        borderRadius: RADIUS,
        bgcolor: COLORS.panel,
        border: "1px solid",
        borderColor: COLORS.border,
        color: COLORS.text,
        ...sx,
      }}
    >
      {children}
    </Comp>
  );
}

/** ---------------- StageLink red button ---------------- */
function RedButton(props: React.ComponentProps<typeof Button>) {
  return (
    <Button
      {...props}
      variant="contained"
      sx={{
        borderRadius: RADIUS,
        fontWeight: 800,
        textTransform: "none",
        bgcolor: COLORS.red,
        "&:hover": { bgcolor: COLORS.redHover },
        ...props.sx,
      }}
    />
  );
}

/** ---------------- Reusable Accordion Panel ---------------- */
function Panel({
  title,
  icon,
  defaultExpanded = false,
  onRemove,
  children,
}: {
  title: string;
  icon?: React.ReactNode;
  defaultExpanded?: boolean;
  onRemove?: () => void;
  children: React.ReactNode;
}) {
  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      disableGutters
      sx={{
        bgcolor: COLORS.panel,
        color: COLORS.text,
        border: "1px solid",
        borderColor: COLORS.border,
        borderRadius: RADIUS,
        "&::before": { display: "none" },
        overflow: "hidden",
        "&.Mui-expanded": { borderColor: COLORS.red },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon htmlColor={COLORS.textMuted} />}
        sx={{
          minHeight: 56,
          "& .MuiAccordionSummary-content": {
            alignItems: "center",
            gap: 1,
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1 }}>
          {icon}
          <Typography sx={{ fontWeight: 800, letterSpacing: "-0.01em" }}>
            {title}
          </Typography>
        </Box>
        {onRemove && (
          <Tooltip title="Remove from layout">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
              sx={{
                color: COLORS.textMuted,
                "&:hover": { color: "#fff", bgcolor: "rgba(255,255,255,0.06)" },
              }}
            >
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </AccordionSummary>
      <Divider sx={{ borderColor: COLORS.border }} />
      <AccordionDetails sx={{ p: 0 }}>
        <Box sx={{ p: 2 }}>{children}</Box>
      </AccordionDetails>
    </Accordion>
  );
}

/** ---------------- Tool registry ---------------- */
type ToolId = "chat" | "guests" | "polls" | "bingo" | "youtube" | "sing4you" | "songlist";

const TOOL_META: Record<
  ToolId,
  { label: string; icon: React.ReactNode }
> = {
  chat: { label: "Live Chat", icon: <ForumIcon fontSize="small" /> },
  guests: { label: "Guest Management", icon: <PeopleIcon fontSize="small" /> },
  polls: { label: "Polls", icon: <PollIcon fontSize="small" /> },
  bingo: { label: "Bingo Break", icon: <CasinoIcon fontSize="small" /> },
  youtube: { label: "YouTube Requests", icon: <YouTubeIcon fontSize="small" /> },
  sing4you: { label: "Sing4You (MR mix)", icon: <MusicVideoIcon fontSize="small" /> },
  songlist: { label: "Song List", icon: <QueueMusicIcon fontSize="small" /> },
};

export interface Sing4YouCallbacks {
  userId: string;
  onKaraokeChange?: (active: boolean, trackName: string) => void;
}

export default function StageLinkManagerLayout({
  children,
  zg,
  sing4YouCallbacks,
}: {
  children: React.ReactNode;
  zg: ZegoExpressEngine | null;
  /** Required to show and use Sing4You tool; provides userId and activate/deactivate callbacks. */
  sing4YouCallbacks?: Sing4YouCallbacks | null;
}) {
  const [params] = useSearchParams();
  const bookingId = params.get("bookingId") ?? "";

  const lgUp = useMediaQuery("(min-width:1200px)");
  const smDown = useMediaQuery("(max-width:899px)");

  const [toolsOpen, setToolsOpen] = useState(false);

  // Song list: talent adds songs via Song List upload; one is "active" in Sing4You for broadcast.
  // Store active by id so we always resolve URL/name from songList (source of truth).
  const [songList, setSongList] = useState<SongItem[]>([]);
  const [activeTrackId, setActiveTrackId] = useState<string | null>(null);

  /** Only add to list when song has a valid URL (from upload); that URL is used when the song is clicked to load in Sing4You. */
  const handleSongAdded = useCallback((song: SongItem) => {
    const url = song?.url != null ? String(song.url).trim() : "";
    if (!url) return;
    setSongList((prev) => [...prev, { ...song, url }]);
    setActiveTools((prev) => (prev.includes("songlist") ? prev : [...prev, "songlist"]));
  }, []);
  const handleSongSelect = useCallback(
    (song: SongItem) => {
      setActiveTrackId(song.id);
      setActiveTools((prev) => (prev.includes("sing4you") ? prev : [...prev, "sing4you"]));
    },
    []
  );
  const handleTrackDuration = useCallback((url: string, duration: number) => {
    setSongList((prev) =>
      prev.map((s) => (s.url === url ? { ...s, duration } : s))
    );
  }, []);
  const handleTrackReady = useCallback((name: string, url: string, duration?: number) => {
    const song: SongItem = {
      id: `song-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      name,
      url,
      duration: duration ?? null,
      addedAt: Date.now(),
    };
    setSongList((prev) => [...prev, song]);
    setActiveTools((prev) => (prev.includes("songlist") ? prev : [...prev, "songlist"]));
  }, []);

  // Active tools: empty by default; talent opens drawer and activates tools via "Show"
  const [activeTools, setActiveTools] = useState<ToolId[]>([]);

  const isActive = useCallback(
    (id: ToolId) => activeTools.includes(id),
    [activeTools]
  );

  const addTool = useCallback(
    (id: ToolId) => {
      setActiveTools((prev) =>
        prev.includes(id) ? prev : [...prev, id]
      );
      if (smDown) setToolsOpen(false); // mobile: close tools after choosing
    },
    [smDown]
  );

  const removeTool = useCallback((id: ToolId) => {
    setActiveTools((prev) => prev.filter((t) => t !== id));
  }, []);

  const toggleTool = useCallback(
    (id: ToolId) => {
      if (isActive(id)) {
        removeTool(id);
      } else {
        addTool(id);
      }
    },
    [isActive, addTool, removeTool]
  );

  /** Render a tool by id */
  const renderTool = useCallback(
    (id: ToolId) => {
      switch (id) {
        case "chat":
          return (
            <Panel
              title={TOOL_META.chat.label}
              icon={TOOL_META.chat.icon}
              defaultExpanded
              onRemove={() => removeTool("chat")}
            >
              <LiveChat
                bookingId={bookingId}
                username="talent"
                height={smDown ? "45vh" : "50vh"}
              />
            </Panel>
          );
        case "guests":
          return (
            <Panel
              title={TOOL_META.guests.label}
              icon={TOOL_META.guests.icon}
              onRemove={() => removeTool("guests")}
            >
              <GuestManagement bookingId={bookingId} />
            </Panel>
          );
        case "polls":
          return (
            <Panel
              title={TOOL_META.polls.label}
              icon={TOOL_META.polls.icon}
              onRemove={() => removeTool("polls")}
            >
              <LivePolls bookingId={bookingId} />
            </Panel>
          );
        case "bingo":
          return (
            <Panel
              title={TOOL_META.bingo.label}
              icon={TOOL_META.bingo.icon}
              onRemove={() => removeTool("bingo")}
            >
              <BingoManagement bookingId={bookingId} />
            </Panel>
          );
        case "youtube":
          return (
            <Panel
              title={TOOL_META.youtube.label}
              icon={TOOL_META.youtube.icon}
              onRemove={() => removeTool("youtube")}
            >
              <YoutubeRequest bookingId={bookingId} />
            </Panel>
          );
        case "sing4you":
          if (!sing4YouCallbacks || !zg) return null;
          return (
            <Panel
              title={TOOL_META.sing4you.label}
              icon={TOOL_META.sing4you.icon}
              onRemove={() => removeTool("sing4you")}
            >
              {/* activeTrack URL is always resolved from songList (backend MP3 URL); same URL loads when song is clicked in list */}
              <Sing4YouManagementUpdated
                zg={zg}
                activeTrack={(() => {
                  if (!activeTrackId) return null;
                  const song = songList.find((s) => s.id === activeTrackId);
                  if (!song) return null;
                  const url = song.url != null ? String(song.url).trim() : "";
                  if (!url) return null;
                  return {
                    url,
                    name: song.name ?? "",
                    duration: song.duration ?? undefined,
                  };
                })()}
                songs={songList}
                onSongSelect={handleSongSelect}
                onKaraokeChange={sing4YouCallbacks.onKaraokeChange}
                onTrackDuration={handleTrackDuration}
                onClearTrack={() => setActiveTrackId(null)}
                onTrackReady={handleTrackReady}
              />
            </Panel>
          );
        case "songlist":
          return (
            <Panel
              title={TOOL_META.songlist.label}
              icon={TOOL_META.songlist.icon}
              onRemove={() => removeTool("songlist")}
            >
              <ListofSongsManagement
                songs={songList}
                activeSongId={activeTrackId}
                onSongAdded={handleSongAdded}
                onSongSelect={handleSongSelect}
                emptyMessage="Add songs using the upload area above, then click a song to load it in Karaoke Studio."
              />
            </Panel>
          );
        default:
          return null;
      }
    },
    [bookingId, removeTool, smDown, sing4YouCallbacks, zg, songList, activeTrackId, handleSongAdded, handleSongSelect, handleTrackDuration, handleTrackReady]
  );

  /** Panels to render on the right (desktop) or below player (mobile/tablet) */
  const renderedPanels = useMemo(
    () => activeTools.map((id) => <Box key={id}>{renderTool(id)}</Box>),
    [activeTools, renderTool]
  );

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: COLORS.bg, color: COLORS.text }}>
      {/* Header */}
      <AppBar
        position="sticky"
        elevation={0}
        color="transparent"
        sx={{
          borderBottom: "1px solid",
          borderColor: COLORS.border,
          backdropFilter: "blur(8px)",
          background:
            "linear-gradient(180deg, rgba(10,10,10,0.9), rgba(10,10,10,0.65))",
        }}
      >
        <Toolbar sx={{ gap: 2 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              bgcolor: COLORS.panelAlt,
              border: "1px solid",
              borderColor: COLORS.border,
            }}
          />
          <Typography variant="h6" sx={{ fontWeight: 900, letterSpacing: "-0.01em" }}>
            StageLink • DJLive
          </Typography>

          <Box
            sx={{
              ml: 1,
              px: 1,
              py: 0.25,
              borderRadius: 999,
              bgcolor: "rgba(229,9,20,0.12)",
              border: "1px solid",
              borderColor: COLORS.red,
              lineHeight: 1,
            }}
          >
            <Typography
              variant="caption"
              sx={{ fontWeight: 900, letterSpacing: "0.08em", color: COLORS.red }}
            >
              LIVE
            </Typography>
          </Box>

          <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1 }}>
            <RedButton
              onClick={() => setToolsOpen(true)}
              startIcon={<BuildRoundedIcon />}
            >
              Tools
            </RedButton>
            <IconButton size="small" aria-label="more" sx={{ color: COLORS.textMuted }}>
              <MoreVertIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Content — tighter padding on desktop so video uses more space */}
      <Box sx={{ p: { xs: 1.5, sm: 2, md: 2 } }}>
        {/* When no tools on desktop: single full-width column; else 8/4 split */}
        {(() => {
          const hasRight = lgUp && activeTools.length > 0;
          return (
            <Grid container spacing={{ xs: 1.5, md: 2 }}>
              {/* LEFT: Player */}
              <Grid
                item
                xs={12}
                lg={hasRight ? 8 : 12}
                sx={{ transition: "all 0.35s cubic-bezier(.4,0,.2,1)" }}
              >
                <Surface
                  sx={{
                    p: 0, overflow: "hidden",
                    width: "100%",
                    maxWidth: "none",
                  }}
                >
                  <Box
                    sx={{
                      position: "relative",
                      width: "100%",
                      aspectRatio: "16 / 9",
                      bgcolor: "#000",
                    }}
                  >
                    <Box sx={{ position: "absolute", inset: 0 }}>{children}</Box>
                  </Box>

                  <Divider sx={{ borderColor: COLORS.border }} />

                  {/* Live Streams Viewer */}
                  <Box sx={{ p: { xs: 1.5, sm: 2 }, pb: 0 }}>
                    <LiveStreamsViewer zg={zg} bookingId={bookingId} />
                  </Box>

                  <Divider sx={{ borderColor: COLORS.border }} />
                  <Box sx={{ p: { xs: 1.5, sm: 2 }, pb: 0 }}>
                    <VideoController zg={zg} />
                  </Box>
                </Surface>

                {/* On ≤lg, show the chosen tools below the player */}
                <Box sx={{ mt: { xs: 1.5, sm: 2 }, display: "grid", gap: { xs: 1.5, sm: 2 } }}>
                  {!lgUp && renderedPanels}
                </Box>
              </Grid>

              {/* RIGHT: tools column — only when tools are active on large screens */}
              {hasRight && (
                <Grid item xs={12} lg={4}>
                  <Box sx={{ display: "grid", gap: 2 }}>{renderedPanels}</Box>
                </Grid>
              )}
            </Grid>
          );
        })()}
      </Box>

      {/* TOOLS DRAWER */}
      <Drawer
        anchor="right"
        open={toolsOpen}
        onClose={() => setToolsOpen(false)}
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          sx: {
            width: smDown ? "85vw" : 460, // not full screen on mobile
            bgcolor: COLORS.bg,
            color: COLORS.text,
            borderLeft: "1px solid",
            borderColor: COLORS.border,
          },
        }}
      >
        {/* Drawer header with close button */}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 1,
            p: 2,
            display: "flex",
            alignItems: "center",
            gap: 1,
            borderBottom: "1px solid",
            borderColor: COLORS.border,
            background:
              "linear-gradient(180deg, rgba(20,20,20,0.95), rgba(20,20,20,0.8))",
            backdropFilter: "blur(8px)",
          }}
        >
          <BuildRoundedIcon htmlColor={COLORS.text} />
          <Typography variant="h6" sx={{ fontWeight: 900 }}>
            Creator Tools
          </Typography>
          <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 0.5 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setToolsOpen(false)}
              sx={{
                borderRadius: RADIUS,
                textTransform: "none",
                fontWeight: 700,
                color: COLORS.text,
                borderColor: COLORS.border,
                "&:hover": { borderColor: COLORS.textMuted },
              }}
            >
              Close
            </Button>
            <IconButton
              onClick={() => setToolsOpen(false)}
              aria-label="close tools"
              sx={{ color: COLORS.textMuted }}
            >
              <CloseRoundedIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Tool toggles */}
        <Box sx={{ p: 2 }}>
          <Surface sx={{ p: 0 }}>
            <List dense sx={{ py: 0 }}>
              {(
                [
                  { id: "chat", meta: TOOL_META.chat },
                  { id: "guests", meta: TOOL_META.guests },
                  { id: "polls", meta: TOOL_META.polls },
                  { id: "bingo", meta: TOOL_META.bingo },
                  { id: "youtube", meta: TOOL_META.youtube },
                  { id: "sing4you", meta: TOOL_META.sing4you },
                  { id: "songlist", meta: TOOL_META.songlist },
                ] as { id: ToolId; meta: { label: string; icon: React.ReactNode } }[]
              ).map(({ id, meta }) => {
                const active = isActive(id);
                return (
                  <ListItem
                    key={id}
                    secondaryAction={
                      <Stack direction="row" spacing={1}>
                        <Button
                          size="small"
                          onClick={() => toggleTool(id)}
                          startIcon={
                            active ? (
                              <RemoveCircleOutlineRoundedIcon fontSize="small" />
                            ) : (
                              <AddCircleOutlineRoundedIcon fontSize="small" />
                            )
                          }
                          sx={{
                            borderRadius: RADIUS,
                            textTransform: "none",
                            fontWeight: 700,
                            px: 1.25,
                            color: active ? COLORS.text : "#fff",
                            bgcolor: active ? "rgba(255,255,255,0.08)" : COLORS.red,
                            "&:hover": {
                              bgcolor: active ? "rgba(255,255,255,0.12)" : COLORS.redHover,
                            },
                            minWidth: 0,
                          }}
                        >
                          {active ? "Remove" : "Show"}
                        </Button>
                      </Stack>
                    }
                  >
                    <ListItemIcon sx={{ minWidth: 36, color: COLORS.textMuted }}>
                      {meta.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography sx={{ fontWeight: 700 }}>{meta.label}</Typography>
                      }
                      secondary={
                        <Typography
                          variant="caption"
                          sx={{ color: COLORS.textMuted }}
                        >
                          {active ? "Visible in layout" : "Hidden"}
                        </Typography>
                      }
                    />
                  </ListItem>
                );
              })}
            </List>
          </Surface>
        </Box>
        {/* (Optional) quick tips / footer */}
        <Box sx={{ p: 2, pt: 0, color: COLORS.textMuted }}>
          <Typography variant="caption">
            Tip: On mobile, selecting a tool will close this drawer and show it in
            the main view. Remove any tool from its panel header.
          </Typography>
        </Box>
      </Drawer>
      {/* Mobile FAB: open Tools */}
      {smDown && (
        <Fab
          onClick={() => setToolsOpen(true)}
          aria-label="open tools"
          sx={{
            position: "fixed",
            right: 16,
            bottom: 16,
            borderRadius: RADIUS,
            fontWeight: 800,
            color: COLORS.text,
            bgcolor: COLORS.red,
            "&:hover": { bgcolor: COLORS.redHover },
          }}
        >
          <BuildRoundedIcon />
        </Fab>
      )}
    </Box>
  );
}
